import React from 'react';
import { Grid3x3, Check } from 'lucide-react';
import { BingoCell } from '../../data/go2LegendeMock';

interface DestinationBingoProps {
  cells: BingoCell[];
}

const countryEmojiMap: Record<string, string> = {
  'Grčka': '🇬🇷',
  'Španija': '🇪🇸',
  'Italija': '🇮🇹',
  'Francuska': '🇫🇷',
  'Turska': '🇹🇷',
  'Crna Gora': '🇲🇪',
  'Češka': '🇨🇿',
  'Mađarska': '🇭🇺',
  'Albanija': '🇦🇱',
};

const checkBingoWin = (cells: BingoCell[]): { hasWin: boolean; winningCells: number[] } => {
  const visited = cells.filter((c) => c.visited).map((c) => c.id);

  // All possible winning lines (rows, columns, diagonals)
  const lines = [
    [1, 2, 3], // Row 1
    [4, 5, 6], // Row 2
    [7, 8, 9], // Row 3
    [1, 4, 7], // Col 1
    [2, 5, 8], // Col 2
    [3, 6, 9], // Col 3
    [1, 5, 9], // Diagonal 1
    [3, 5, 7], // Diagonal 2
  ];

  const winningCells: number[] = [];
  let hasWin = false;

  for (const line of lines) {
    const isLineComplete = line.every((id) => visited.includes(id));
    if (isLineComplete) {
      hasWin = true;
      winningCells.push(...line);
    }
  }

  return { hasWin, winningCells: [...new Set(winningCells)] };
};

export const DestinationBingo: React.FC<DestinationBingoProps> = ({ cells }) => {
  const visitedCount = cells.filter((c) => c.visited).length;
  const totalCells = cells.length;
  const { hasWin, winningCells } = checkBingoWin(cells);

  // Calculate completed lines count
  const lines = [
    [1, 2, 3], [4, 5, 6], [7, 8, 9],
    [1, 4, 7], [2, 5, 8], [3, 6, 9],
    [1, 5, 9], [3, 5, 7],
  ];
  const visited = cells.filter((c) => c.visited).map((c) => c.id);
  const completedLinesCount = lines.filter((line) =>
    line.every((id) => visited.includes(id))
  ).length;

  const allVisited = visitedCount === totalCells;

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Grid3x3 className="w-6 h-6 text-go2-blue" />
          <h2 className="text-2xl font-bold text-gray-900">Destinacijski Bingo</h2>
        </div>
        <p className="text-gray-600">Poseti destinacije i osvoji nagrade!</p>
      </div>

      {/* Win Celebration */}
      {hasWin && (
        <div className="mb-6 p-4 bg-gradient-to-r from-go2-yellow/20 to-go2-yellow/10 border-2 border-go2-yellow rounded-lg">
          <p className="text-lg font-bold text-gray-900 text-center">
            Red završen! +350 bonus bodova! 🎉
          </p>
        </div>
      )}

      {/* All Visited (BINGO) */}
      {allVisited && (
        <div className="mb-6 p-5 bg-gradient-to-r from-brand-500 to-brand-600 rounded-xl text-white text-center">
          <p className="text-2xl font-bold mb-1">🎊 BINGO! 🎊</p>
          <p className="text-lg">Svih 9 destinacija!</p>
          <p className="text-xl font-bold mt-2">Mega Nagrada: 1000 bodova!</p>
        </div>
      )}

      {/* 3x3 Bingo Grid */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {cells.map((cell) => {
          const isWinningCell = winningCells.includes(cell.id);
          const emoji = countryEmojiMap[cell.country] || '🌍';

          return (
            <div
              key={cell.id}
              className={`
                aspect-square p-4 rounded-lg flex flex-col items-center justify-center text-center
                transition-all duration-200 hover:scale-105
                ${
                  cell.visited
                    ? 'bg-emerald-100 border-2 border-emerald-400'
                    : 'bg-gray-50 border-2 border-dashed border-gray-300'
                }
                ${isWinningCell ? 'ring-2 ring-go2-yellow shadow-lg' : ''}
              `}
            >
              {/* Country Emoji */}
              <div className="text-3xl mb-2">{emoji}</div>

              {/* Destination Name */}
              <div
                className={`text-sm font-semibold ${
                  cell.visited ? 'text-emerald-800' : 'text-gray-400'
                }`}
              >
                {cell.destination}
              </div>

              {/* Check Mark for Visited */}
              {cell.visited && (
                <div className="mt-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom Stats */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Posećeno:</span>
          <span className="font-semibold text-gray-900">
            {visitedCount} / {totalCells} destinacija
          </span>
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Završeni redovi:</span>
          <span className="font-semibold text-gray-900">{completedLinesCount} / 8</span>
        </div>
      </div>

      {/* Refresh Note */}
      <div className="text-xs text-gray-500 text-center pt-4 border-t border-gray-100">
        Bingo tabela se osvežava kvartalno
      </div>
    </div>
  );
};
